import { useState } from "react";
import { CheckIcon, PlusIcon } from "@radix-ui/react-icons";
import {
  OrganizationPublicResponse,
  PublicName,
  PublicNameAndEmail,
} from "client/openapi";
import { phoneLabel, Verified } from "./publicBooking";

/** Who a booking is for, as the server takes it. */
export type Who =
  | { student_id: number }
  | { new_student: PublicName; new_parent?: PublicNameAndEmail }
  | { for_self: PublicNameAndEmail };

/** Who joins a class, as the server takes it: one or more people. */
export type Enrollees = {
  student_ids: number[];
  new_student?: PublicName;
  new_parent?: PublicNameAndEmail;
  for_self?: PublicNameAndEmail;
};

const NEW = -1;
const INPUT = "mt-1.5 w-full rounded-lg border border-gray-300 px-3.5 py-2.5";
const LABEL = "text-xs font-semibold text-gray-600";

function Field({
  id,
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className={LABEL}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className={INPUT}
      />
    </div>
  );
}

/** How to reach the org, for people who can't book online. */
export function ContactSentence({ org }: { org: OrganizationPublicResponse }) {
  const ways = [
    org.public_phone_number && phoneLabel(org.public_phone_number),
    org.public_email,
  ].filter(Boolean);
  return ways.length ? (
    <>
      contact {org.name} at {ways.join(" or ")}
    </>
  ) : (
    <>contact {org.name}</>
  );
}

type Shared = {
  org: OrganizationPublicResponse;
  verified: Verified;
  returningOnly: boolean;
  accent: string;
  busy: boolean;
};

/**
 * After the code: a returning family picks who the session is for (or adds
 * someone new); a new family says who is booking. For a class, a returning
 * family can tick several people.
 */
export default function WhoStep(
  props: Shared &
    (
      | { multiple?: false; onBook: (who: Who) => void }
      | {
          multiple: true;
          /** The button, from the first names of who is ticked. */
          label: (firstNames: string[]) => string;
          onEnroll: (enrollees: Enrollees) => void;
        }
    )
) {
  const { org, verified, returningOnly, accent, busy } = props;
  const verb = props.multiple ? "enroll" : "book";
  // The number belongs to someone here, even with no student left to book.
  const known = verified.is_known;
  const [picked, setPicked] = useState<number[]>([
    verified.students[0]?.id ?? NEW,
  ]);
  const [forChild, setForChild] = useState(true);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [childFirst, setChildFirst] = useState("");
  const [childLast, setChildLast] = useState("");

  // Who can't book online here, and why; each is told how to reach the org.
  const blocked = verified.archived_only
    ? `We found this number, but we can't ${verb} online for it. Please `
    : !known && returningOnly
    ? `We couldn't find this number. To ${verb} for the first time, `
    : known && returningOnly && !verified.students.length
    ? `There's no one on file to ${verb}. To add someone, `
    : undefined;
  if (blocked) {
    return (
      <p className="mt-6 rounded-lg bg-gray-50 p-4 text-sm leading-relaxed text-gray-700">
        {blocked}
        <ContactSentence org={org} />.
      </p>
    );
  }

  const filled = (...values: string[]) => values.every((v) => v.trim());
  // A child's last name defaults to the parent's.
  const child = { first_name: childFirst, last_name: childLast || last };
  // Someone new: their names, once filled in (and, for a family on file,
  // only while Someone new is ticked).
  const addingNew = !known || picked.includes(NEW);
  let someoneNew: Omit<Enrollees, "student_ids"> | undefined;
  if (known && addingNew && filled(childFirst, childLast))
    someoneNew = {
      new_student: { first_name: childFirst, last_name: childLast },
    };
  else if (!known && forChild && filled(first, last, email, childFirst))
    someoneNew = {
      new_student: child,
      new_parent: { first_name: first, last_name: last, email },
    };
  else if (!known && !forChild && filled(first, last, email))
    someoneNew = { for_self: { first_name: first, last_name: last, email } };

  const studentIds = known ? picked.filter((id) => id !== NEW) : [];
  const chosen: Enrollees | undefined =
    addingNew && !someoneNew
      ? undefined
      : studentIds.length || someoneNew
      ? { student_ids: studentIds, ...someoneNew }
      : undefined;
  const firstNames = [
    ...verified.students
      .filter((s) => studentIds.includes(s.id))
      .map((s) => s.first_name),
    ...(chosen?.new_student
      ? [chosen.new_student.first_name]
      : chosen?.for_self
      ? [chosen.for_self.first_name]
      : []),
  ];

  const submit = () => {
    if (!chosen) return;
    if (props.multiple) return props.onEnroll(chosen);
    const { student_ids, ...rest } = chosen;
    props.onBook(
      student_ids.length ? { student_id: student_ids[0] } : (rest as Who)
    );
  };
  const pick = (id: number) =>
    setPicked((current) =>
      !props.multiple
        ? [id]
        : current.includes(id)
        ? current.filter((p) => p !== id)
        : [...current, id]
    );
  // A radio dot, or a check box when several can be picked.
  const mark = (on: boolean) =>
    props.multiple ? (
      <span
        className="flex h-4 w-4 items-center justify-center rounded border-2 text-white"
        style={{
          borderColor: on ? accent : "#D1D5DB",
          backgroundColor: on ? accent : undefined,
        }}
      >
        {on && <CheckIcon />}
      </span>
    ) : (
      <span
        className="flex h-4 w-4 items-center justify-center rounded-full border-2"
        style={{ borderColor: on ? accent : "#D1D5DB" }}
      >
        {on && (
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: accent }}
          />
        )}
      </span>
    );
  const option = props.multiple ? "checkbox" : "radio";

  const segment = (on: boolean) =>
    on ? { backgroundColor: accent, color: "white" } : undefined;

  return (
    <div className="mt-6">
      {known ? (
        <>
          <h2 className="text-xl font-bold">
            {verified.family_first_name
              ? `Welcome back, ${verified.family_first_name}`
              : "Welcome back"}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {props.multiple ? "Who is enrolling?" : "Who is this session for?"}
          </p>
          <div
            className="mt-3 flex flex-col gap-2"
            role={props.multiple ? "group" : "radiogroup"}
          >
            {verified.students.map((student) => (
              <button
                key={student.id}
                type="button"
                role={option}
                aria-checked={picked.includes(student.id)}
                onClick={() => pick(student.id)}
                className="flex items-center gap-3 rounded-lg border px-4 py-3 text-left font-medium"
                style={
                  picked.includes(student.id)
                    ? { borderColor: accent, backgroundColor: `${accent}0D` }
                    : { borderColor: "#E5E7EB" }
                }
              >
                {mark(picked.includes(student.id))}
                {student.first_name} {student.last_name}
              </button>
            ))}
            {!returningOnly && (
              <button
                type="button"
                role={option}
                aria-checked={picked.includes(NEW)}
                onClick={() => pick(NEW)}
                className="flex items-center gap-3 rounded-lg border px-4 py-3 text-left font-semibold"
                style={{
                  color: accent,
                  borderColor: picked.includes(NEW) ? accent : "#E5E7EB",
                }}
              >
                <PlusIcon /> Someone new
              </button>
            )}
          </div>
          {picked.includes(NEW) && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field
                id="child-first"
                label="First name"
                value={childFirst}
                onChange={setChildFirst}
              />
              <Field
                id="child-last"
                label="Last name"
                value={childLast}
                onChange={setChildLast}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            New here? Tell us who is {props.multiple ? "enrolling" : "booking"}.
          </p>
          <div className="mt-2 inline-flex overflow-hidden rounded-lg border border-gray-300 text-sm font-semibold">
            <button
              type="button"
              className="px-3 py-1.5"
              style={segment(forChild)}
              onClick={() => setForChild(true)}
            >
              For my child
            </button>
            <button
              type="button"
              className="px-3 py-1.5"
              style={segment(!forChild)}
              onClick={() => setForChild(false)}
            >
              For myself
            </button>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field
              id="first"
              label={forChild ? "Your first name" : "First name"}
              value={first}
              onChange={setFirst}
              autoComplete="given-name"
            />
            <Field
              id="last"
              label={forChild ? "Your last name" : "Last name"}
              value={last}
              onChange={setLast}
              autoComplete="family-name"
            />
            <div className="sm:col-span-2">
              <Field
                id="email"
                type="email"
                label={forChild ? "Your email" : "Email"}
                value={email}
                onChange={setEmail}
                autoComplete="email"
              />
            </div>
            {forChild && (
              <>
                <Field
                  id="child-first"
                  label="Student first name"
                  value={childFirst}
                  onChange={setChildFirst}
                />
                <Field
                  id="child-last"
                  label="Student last name"
                  value={childLast}
                  onChange={setChildLast}
                  placeholder={last}
                />
              </>
            )}
          </div>
        </>
      )}

      <button
        type="button"
        disabled={!chosen || busy}
        onClick={submit}
        style={{ backgroundColor: accent }}
        className="mt-6 w-full rounded-lg px-8 py-3.5 text-base font-semibold text-white disabled:opacity-40 sm:w-auto"
      >
        {props.multiple
          ? busy
            ? "Enrolling…"
            : props.label(firstNames)
          : busy
          ? "Booking…"
          : "Book session"}
      </button>
      <p className="mt-2 text-xs text-gray-500">
        {props.multiple
          ? `${org.name} will be in touch about billing.`
          : known && verified.email_hint
          ? `Confirmations go to ${verified.email_hint} and this number.`
          : `Your details go only to ${org.name}.`}
      </p>
    </div>
  );
}
